import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Instagram, X } from "lucide-react";
import { INSTAGRAM_URL } from "../assets";
import { track } from "../utils/analytics";

const easeSoft = [0.4, 0, 0.2, 1];

export function useGagneParam() {
  return React.useMemo(() => {
    if (typeof window === "undefined") return false;
    const val = new URLSearchParams(window.location.search).get("gagne");
    return !!val && val.toLowerCase() === "true";
  }, []);
}

export default function WinModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const code = new URLSearchParams(window.location.search).get("code");
    const valid = code && /^FIGHT0[1-6]$/i.test(code.trim()) ? code.trim().toUpperCase() : null;
    track("qr_win_landing", valid ? { code: valid } : undefined);
    if (valid) {
      const num = parseInt(valid.slice(-2), 10);
      // Distinct event name per code → visible directly in GA4 reports without custom dimensions.
      track(`qr_scan_${valid.toLowerCase()}`, { code: valid, code_number: num });
      track("qr_code_found", { code: valid, code_number: num, event_label: valid });
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/qr-hunt/scan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: valid }),
      }).catch(() => {});
    }
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="win-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7, ease: easeSoft }}
          className="fixed inset-0 z-[100] flex items-center justify-center px-5"
          style={{ backgroundColor: "rgba(5, 6, 5, 0.82)", backdropFilter: "blur(6px)" }}
          onClick={onClose}
        >
          <motion.div
            data-testid="win-modal"
            initial={{ opacity: 0, y: 32, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.98 }}
            transition={{ duration: 0.7, delay: 0.15, ease: easeSoft }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-ink border border-leaf/40 px-6 py-10 sm:px-10 sm:py-12 text-center"
          >
            <button
              data-testid="win-modal-close-icon"
              onClick={onClose}
              aria-label="Fermer"
              className="absolute top-4 right-4 text-bone/60 hover:text-bone p-2 transition-colors"
            >
              <X size={18} strokeWidth={1.5} />
            </button>

            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-leaf/50 bg-leaf/10">
              <Trophy size={28} strokeWidth={1.5} className="text-leaf" />
            </div>

            <p translate="no" className="notranslate text-[10px] tracking-[0.45em] uppercase text-bone/50 mb-4">Fight-Lens</p>

            <h2
              data-testid="win-modal-title"
              className="font-serif font-light text-bone leading-snug tracking-tight text-2xl sm:text-3xl"
            >
              Bien joué, <span className="italic">tu as gagné&nbsp;!</span>
            </h2>

            <p className="mt-6 text-ash text-sm sm:text-base leading-relaxed">
              Pour réclamer ton <span className="text-bone">shooting gratuit</span> (2 photos
              offertes), contacte-moi sur Instagram en envoyant la photo du QR code que tu as
              trouvé.
            </p>

            <p translate="no" className="notranslate mt-4 font-display text-xl tracking-wider text-leaf">@nono.lcv</p>

            <div className="mt-8 flex flex-col sm:flex-row items-stretch justify-center gap-3">
              <a
                data-testid="win-modal-instagram-btn"
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("qr_win_instagram_click")}
                className="inline-flex items-center justify-center gap-3 bg-leaf/15 border border-leaf hover:bg-leaf/25 transition-colors duration-300 px-6 py-4"
              >
                <Instagram size={16} strokeWidth={1.5} className="text-bone" />
                <span className="text-[11px] tracking-[0.28em] uppercase text-bone">
                  Réclamer sur Instagram
                </span>
              </a>
              <button
                data-testid="win-modal-close-btn"
                onClick={onClose}
                className="inline-flex items-center justify-center border border-bone/25 hover:border-bone/60 transition-colors duration-300 px-6 py-4"
              >
                <span className="text-[11px] tracking-[0.28em] uppercase text-bone/70">Fermer</span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
